import React, { useEffect, useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { hasSideAdCampaign, sideAds } from '../config/ads';
import '../styles/PublicPageLayout.css';

const useDesktopAdLayout = (minWidth) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(min-width: ${minWidth}px)`);
    const updateMatch = () => setMatches(mediaQuery.matches);

    updateMatch();
    mediaQuery.addEventListener('change', updateMatch);

    return () => mediaQuery.removeEventListener('change', updateMatch);
  }, [minWidth]);

  return matches;
};

const useHeaderViewportOffset = () => {
  const [offset, setOffset] = useState(0);

  useLayoutEffect(() => {
    const header = document.querySelector('.header');

    if (!header) return undefined;

    let frameId = null;

    const measure = () => {
      frameId = null;
      const nextOffset = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
      setOffset((currentOffset) => (
        currentOffset === nextOffset ? currentOffset : nextOffset
      ));
    };

    const scheduleMeasure = () => {
      if (frameId === null) frameId = window.requestAnimationFrame(measure);
    };

    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(scheduleMeasure)
      : null;

    measure();
    resizeObserver?.observe(header);
    window.addEventListener('resize', scheduleMeasure);
    window.addEventListener('scroll', scheduleMeasure, { passive: true });

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
      window.removeEventListener('scroll', scheduleMeasure);
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, []);

  return offset;
};

const PublicPageLayout = ({ children }) => {
  const isDesktop = useDesktopAdLayout(sideAds.desktopMinWidth);
  const headerViewportOffset = useHeaderViewportOffset();
  const hasCampaign = hasSideAdCampaign(sideAds);
  const showSkin = isDesktop && hasCampaign;

  return (
    <div
      className={`public-page${showSkin ? ' public-page--with-skin' : ''}`}
      data-skin={showSkin ? 'visible' : 'hidden'}
      style={{ '--bigsport-header-height': `${headerViewportOffset}px` }}
    >
      {showSkin && (
        <div
          className="skin-background skin-background--left"
          aria-hidden="true"
          style={{
            '--skin-background-image': `url("${sideAds.left.image}")`,
            '--skin-background-size': sideAds.backgroundSize,
          }}
        />
      )}

      <div className="public-page-content">
        {children}
      </div>

      {showSkin && (
        <div
          className="skin-background skin-background--right"
          aria-hidden="true"
          style={{
            '--skin-background-image': `url("${sideAds.right.image}")`,
            '--skin-background-size': sideAds.backgroundSize,
          }}
        />
      )}
    </div>
  );
};

PublicPageLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default PublicPageLayout;
